<script lang="ts" setup>
import { Co2QuizQuestion } from '@/models/co2-quiz.model';
import { PropType, ref } from 'vue';

const props = defineProps({
  modelValue: {
    type: Object as PropType<Co2QuizQuestion>,
    required: true,
  },
  questions: {
    type: Array as PropType<Co2QuizQuestion[]>,
    required: true,
  },
});

const value = ref<Co2QuizQuestion>({
  ...props.modelValue,
  partQuestions: [
    ...props.questions.filter(
      (q) => q.partQuestion?.id === props.modelValue.id,
    ),
  ],
});

const emit = defineEmits(['update:modelValue']);

const update = () => {
  emit('update:modelValue', value.value);
};

const showAddQuestions = ref<boolean>(false);
const addableQuestions = ref<Co2QuizQuestion[]>([]);

function toggleAddQuestions() {
  showAddQuestions.value = !showAddQuestions.value;
}

function addParts() {
  if (addableQuestions.value) {
    value.value.partQuestions = value.value.partQuestions = [
      ...(value.value.partQuestions || []),
      ...addableQuestions.value,
    ];
    update();
  }
  addableQuestions.value = [];
}

function removePart(index: number) {
  const list = [...(value.value.partQuestions || [])];

  list.splice(index, 1);

  value.value.partQuestions = list;
  update();
}
</script>
<template>
  <v-container>
    <h3>Parts</h3>
    <v-alert
      type="info"
      density="compact"
      v-if="!(value.partQuestions || []).length"
    >
      Parts will be displayed on the same page as this question. This question
      will serve only as the page, not as an actual question.
    </v-alert>

    <v-container>
      <v-card
        class="mb-2"
        style="padding: 8px"
        v-for="(q, index) in value.partQuestions || []"
        :key="q.id"
      >
        {{ q.title }}
        <v-btn
          icon="mdi-delete-outline"
          class="remove-button"
          density="compact"
          @click.prevent="removePart(index)"
        ></v-btn>
      </v-card>
    </v-container>

    <v-dialog width="auto" scrollable>
      <template v-slot:activator="{ props: activatorProps }">
        <v-btn
          v-bind="activatorProps"
          @click="toggleAddQuestions"
          color="success"
          >Add part</v-btn
        >
      </template>
      <template v-slot:default="{ isActive }">
        <v-card>
          <v-card-text>
            <v-radio-group
              multiple
              v-model="addableQuestions"
              messages="Select questions to add"
            >
              <v-radio
                v-for="question in [...questions].filter(
                  (q) =>
                    !(value.partQuestions || [])
                      .map((pq) => pq.id)
                      .includes(q.id),
                )"
                :key="question.id"
                :label="question.title"
                :value="question"
              ></v-radio>
            </v-radio-group>
          </v-card-text>

          <v-divider></v-divider>
          <v-card-actions>
            <v-btn text="Close" @click="isActive.value = false"></v-btn>

            <v-spacer></v-spacer>
            <v-btn
              @click="
                isActive.value = false;
                addParts();
              "
              color="success"
              >Add parts</v-btn
            >
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </v-container>
</template>
<style scoped>
.answer {
  display: flex;
  flex-direction: row;
  padding-right: 16px;
}
.remove-button {
  position: absolute;
  top: 4px;
  right: 8px;
}
</style>
